import { getModalStyles, DEFAULT_COLOR } from './styles.js';
import { createIframe } from './iframe.js';
import type { CalAttrs } from './types.js';

/**
 * Shadow-DOM modal — CSS isolation from host page.
 *
 * Features:
 *   - Closes on Esc key
 *   - Closes on backdrop click
 *   - Closes when iframe sends cal:close or cal:booked
 *   - Locks body scroll while open (via overflow:hidden on <body>)
 *   - Animated open/close (respects prefers-reduced-motion)
 */

let openModal: ModalHandle | null = null;

export interface ModalOptions {
  iframeSrc: string;
  calOrigin: string;
  attrs: CalAttrs;
  onBooked?: (bookingId: string) => void;
}

interface ModalHandle {
  close: () => void;
}

export function openBookingModal(opts: ModalOptions): ModalHandle {
  // Only one modal at a time
  if (openModal) {
    openModal.close();
  }

  const primaryColor = opts.attrs.color ?? DEFAULT_COLOR;

  // Host element (appended to body)
  const host = document.createElement('div');
  host.setAttribute('data-cal-modal', '');
  document.body.appendChild(host);

  // Shadow root — full CSS isolation
  const shadow = host.attachShadow({ mode: 'open' });

  // Inject styles
  const styleEl = document.createElement('style');
  styleEl.textContent = getModalStyles(primaryColor);
  shadow.appendChild(styleEl);

  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.className = 'cal-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', 'Booking');
  shadow.appendChild(backdrop);

  // Modal container
  const modal = document.createElement('div');
  modal.className = 'cal-modal';
  backdrop.appendChild(modal);

  // Header with close button
  const header = document.createElement('div');
  header.className = 'cal-modal-header';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'cal-close-btn';
  closeBtn.setAttribute('aria-label', 'Close booking');
  closeBtn.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
    </svg>
  `;
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // Iframe wrapper
  const iframeWrap = document.createElement('div');
  iframeWrap.className = 'cal-iframe-wrap';
  modal.appendChild(iframeWrap);

  // Create iframe
  const iframeHandle = createIframe({
    src: opts.iframeSrc,
    calOrigin: opts.calOrigin,
    attrs: opts.attrs,
    onResize(height) {
      // Clamp to available viewport
      const maxH = window.innerHeight - 80;
      iframeWrap.style.height = `${Math.min(height, maxH)}px`;
    },
    onBooked(bookingId) {
      opts.onBooked?.(bookingId);
      // Auto-close after a short delay to let the success screen render
      setTimeout(() => handle.close(), 1800);
    },
    onClose() {
      handle.close();
    },
  });
  iframeWrap.appendChild(iframeHandle.wrapper);

  // Lock body scroll
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  function cleanup(): void {
    document.body.style.overflow = prevOverflow;
    document.removeEventListener('keydown', onKeydown);
    iframeHandle.destroy();
    host.remove();
    openModal = null;
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') handle.close();
  }

  // Backdrop click — close only if clicking the backdrop itself, not the modal
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) handle.close();
  });

  closeBtn.addEventListener('click', () => handle.close());

  document.addEventListener('keydown', onKeydown);

  // Move focus into modal (accessibility)
  requestAnimationFrame(() => closeBtn.focus());

  const handle: ModalHandle = {
    close: cleanup,
  };

  openModal = handle;
  return handle;
}
